// Dashboard › Deployment › "Outside Approved – Redeploy": the people still deployed at
// a store that has been marked outside the approved list, and so are due to be moved.
// Derived from GET /stores alone — each row carries the mark (redeploy_pending) and its
// roster, every employee with role, reporting manager and whether they are still on
// the 1Office roster.

const DAY_MS = 86_400_000;

/** The fields of one GET /stores row this list reads. */
export interface RedeployPendingStoreInput {
  id?: string | null;
  store_name?: string | null;
  store_code?: string | null;
  city?: string | null;
  store_status?: string | null;
  redeploy_pending?: boolean | null;
  redeploy_pending_since?: string | null;
  employees?: Array<{
    employee_code?: string | number | null;
    employee_name?: string | null;
    role_name?: string | null;
    reporting_manager_name?: string | null;
    reporting_manager_code?: string | null;
    is_active?: boolean | null;
  }> | null;
}

/** One employee waiting to be moved out of one store. */
export interface RedeployPendingRow {
  storeId: string;
  storeName: string;
  storeCode: string;
  city: string;
  employeeCode: string;
  employeeName: string;
  roleName: string | null;
  reportingManagerName: string | null;
  reportingManagerCode: string | null;
  /** When the store was marked (redeploy_pending_since, as sent). null if unknown.
   *  Read it with parseRedeployTimestamp, not `new Date`. */
  markedOn: string | null;
}

// "2026-10-06 14:28:57.408123+05:30" / "...+00": a timestamptz as the pg driver hands
// it over, raw. The API sends ISO UTC, but if that conversion is ever missed this is
// what arrives, and browsers do not agree on it — so it is rebuilt as ISO before
// parsing rather than left to `new Date`.
const TIMESTAMP_RE = /^(\d{4}-\d{2}-\d{2})[T\s]+(\d{2}:\d{2})(?::(\d{2}))?(?:\.(\d+))?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i;

/** redeploy_pending_since as epoch ms. null when blank or unreadable — never NaN. */
export const parseRedeployTimestamp = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  const m = TIMESTAMP_RE.exec(text);
  let iso = text;
  if (m) {
    const [, day, hourMinute, seconds = '00', fraction = '', zone = ''] = m;
    const millis = fraction ? `.${fraction.slice(0, 3).padEnd(3, '0')}` : '';
    // "+00" → "+00:00", "+0530" → "+05:30".
    const offset = /^z$/i.test(zone)
      ? 'Z'
      : zone.length === 3 ? `${zone}:00` : zone.length === 5 ? `${zone.slice(0, 3)}:${zone.slice(3)}` : zone;
    iso = `${day}T${hourMinute}:${seconds}${millis}${offset}`;
  }
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Is this store awaiting redeployment? The same rule the API's `status_display` uses:
 * the mark only counts on a store that is stored 'Active'. A store closed some other
 * way has its mark cleared server-side; this keeps a stray one off the list regardless.
 */
export const isRedeployPendingStore = (
  store: Pick<RedeployPendingStoreInput, 'redeploy_pending' | 'store_status'>,
): boolean =>
  store.redeploy_pending === true && (store.store_status ?? '').trim().toLowerCase() === 'active';

/**
 * One row per ACTIVE employee at each store awaiting redeployment, the longest wait
 * first.
 *
 * Only an explicit `is_active: false` means the person has left 1Office (their mapping
 * is a leftover the next roster sync removes) — there is nobody to move, so no row. A
 * missing flag reads as active, as it does on the Stores page: the API never sends
 * false when its roster lookup failed, and an older API does not send the flag at all.
 */
export const deriveRedeployPendingRows = (
  stores: readonly RedeployPendingStoreInput[],
): RedeployPendingRow[] => {
  const rows: RedeployPendingRow[] = [];
  for (const store of stores) {
    const storeId = store.id ? String(store.id) : '';
    if (!storeId || !isRedeployPendingStore(store)) continue;
    for (const emp of store.employees ?? []) {
      const employeeCode = emp.employee_code == null ? '' : String(emp.employee_code).trim();
      if (!employeeCode || emp.is_active === false) continue;
      rows.push({
        storeId,
        storeName: (store.store_name ?? '').trim(),
        storeCode: (store.store_code ?? '').trim(),
        city: (store.city ?? '').trim(),
        employeeCode,
        employeeName: (emp.employee_name ?? '').trim(),
        roleName: emp.role_name?.trim() || null,
        reportingManagerName: emp.reporting_manager_name?.trim() || null,
        reportingManagerCode: emp.reporting_manager_code?.trim() || null,
        markedOn: store.redeploy_pending_since || null,
      });
    }
  }
  // Longest-waiting first — the top of the list is who to move next. An unknown date
  // sinks to the bottom rather than posing as the oldest.
  return rows.sort((a, b) => {
    const ta = parseRedeployTimestamp(a.markedOn);
    const tb = parseRedeployTimestamp(b.markedOn);
    if (ta !== tb) {
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta - tb;
    }
    return a.storeName.localeCompare(b.storeName) || a.employeeName.localeCompare(b.employeeName);
  });
};

/**
 * Whole calendar days since the store was marked, in the viewer's timezone — marked
 * late yesterday reads 1 this morning, not 0. null when the date is unknown,
 * unreadable or in the future.
 */
export const redeployWaitingDays = (markedOn?: string | null, now: Date = new Date()): number | null => {
  const ms = parseRedeployTimestamp(markedOn);
  if (ms === null) return null;
  const marked = new Date(ms);
  const from = new Date(marked.getFullYear(), marked.getMonth(), marked.getDate()).getTime();
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  // Rounded: two local midnights are not always 24h apart where clocks change.
  const days = Math.round((to - from) / DAY_MS);
  return days < 0 ? null : days;
};

/** The wait as the cell prints it: "Marked today", "1 day waiting", "12 days waiting". '' when unknown. */
export const formatRedeployWaiting = (days: number | null): string => {
  if (days === null) return '';
  if (days === 0) return 'Marked today';
  return `${days.toLocaleString('en-IN')} day${days === 1 ? '' : 's'} waiting`;
};
